import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../theme/ThemeContext';
import { ROOM_COLORS, type Appointment, type CalendarEvent, type Doctor } from '../types';
import { formatTime, getAppointmentBlockColor, getReadableTextColor, isSameDay } from '../utils';
import { getReservationFeeState } from '../reservationFee';

type ScheduleItem =
  | { kind: 'appointment'; app: Appointment; allDay: boolean; sortKey: number }
  | { kind: 'event'; ev: CalendarEvent; sortKey: number };

interface PhoneScheduleViewProps {
  days: Date[];
  nowTick: number;
  doctors: Doctor[];
  getTimedAppointments: (day: Date) => Appointment[];
  getAllDayAppointments: (day: Date) => Appointment[];
  getEvents: (day: Date) => CalendarEvent[];
  getPatientName: (app: Appointment) => string;
  getReasonLabel: (app: Appointment) => string;
  onOpenAppointment: (app: Appointment, event: React.MouseEvent) => void;
  onAppointmentContextMenu: (app: Appointment, event: React.MouseEvent) => void;
  onEventClick: (ev: CalendarEvent, event: React.MouseEvent) => void;
  onDayClick: (day: Date) => void;
  onLoadMore?: () => void;
}

const LONG_PRESS_MS = 500;

export const PhoneScheduleView: React.FC<PhoneScheduleViewProps> = ({
  days,
  nowTick,
  doctors,
  getTimedAppointments,
  getAllDayAppointments,
  getEvents,
  getPatientName,
  getReasonLabel,
  onOpenAppointment,
  onAppointmentContextMenu,
  onEventClick,
  onDayClick,
  onLoadMore
}) => {
  const { t, i18n } = useTranslation();
  const { tokens } = useTheme();
  const chrome = tokens.calendar;
  const today = new Date(nowTick);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const longPressRef = useRef<{ timer: number; fired: boolean } | null>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !onLoadMore || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) onLoadMore();
    }, { rootMargin: '200px' });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [onLoadMore, days.length]);

  const startLongPress = (app: Appointment, event: React.PointerEvent) => {
    if (event.pointerType === 'mouse') return;
    const { clientX, clientY } = event;
    const timer = window.setTimeout(() => {
      if (longPressRef.current) longPressRef.current.fired = true;
      onAppointmentContextMenu(app, { clientX, clientY, preventDefault: () => {} } as React.MouseEvent);
    }, LONG_PRESS_MS);
    longPressRef.current = { timer, fired: false };
  };
  const cancelLongPress = () => {
    if (longPressRef.current) window.clearTimeout(longPressRef.current.timer);
  };

  const sections = days.map(day => {
    const items: ScheduleItem[] = [
      ...getAllDayAppointments(day).map(app => ({ kind: 'appointment' as const, app, allDay: true, sortKey: -1 })),
      ...getTimedAppointments(day).map(app => ({ kind: 'appointment' as const, app, allDay: false, sortKey: new Date(app.startTime!).getTime() })),
      ...getEvents(day)
        .filter(ev => !ev.isNewPatientIntake)
        .map(ev => ({ kind: 'event' as const, ev, sortKey: new Date(ev.startTime!).getTime() }))
    ].sort((a, b) => a.sortKey - b.sortKey);
    return { day, items, isToday: isSameDay(day, today) };
  }).filter(section => section.items.length > 0 || section.isToday);

  const renderAppointment = (app: Appointment, allDay: boolean) => {
    const isProspect = !!app.isProspect;
    const isCanceled = !isProspect && ((app.status || '').toLowerCase() === 'canceled' || !!app.canceledAt);
    const isNoShow = !isProspect && !!app.isNoShow;
    const isConfirmed = !isProspect && !isCanceled && !isNoShow && (app.status || '').toLowerCase() === 'confirmed';
    const start = app.startTime ? new Date(app.startTime) : null;
    const end = app.endTime ? new Date(app.endTime) : null;
    const untilStart = start ? start.getTime() - nowTick : -1;
    const isUnconfirmedSoon = !isConfirmed && !isCanceled && !isNoShow && untilStart > 0 && untilStart < 24 * 60 * 60 * 1000;
    const isPast = !!end && end.getTime() < nowTick;
    const blockColor = getAppointmentBlockColor(app, doctors);
    const background = isCanceled ? '#fee2e2' : isNoShow ? '#d1d5db' : blockColor;
    const color = isProspect ? '#7c2d12' : (isCanceled || isNoShow) ? '#1f2937' : getReadableTextColor(blockColor);
    const roomColor = app.room ? ROOM_COLORS[app.room] : undefined;
    const reservation = getReservationFeeState(app);
    const showReservationDue = !isPast && reservation.configured && !reservation.isPaid;
    const timeLabel = allDay || !start
      ? t('calendar.allDay')
      : `${formatTime(start)}${end ? ` – ${formatTime(end)}` : ''}`;
    const reason = getReasonLabel(app);
    const border = isProspect
      ? '2px dashed #f59e0b'
      : isUnconfirmedSoon ? '2px solid #dc2626' : isConfirmed ? '2px solid #22c55e' : '1px solid transparent';

    return (
      <button
        key={`a-${app.id}`}
        type="button"
        className="phone-cal-card"
        data-testid={`phone-schedule-appointment-${app.id}`}
        onClick={event => {
          if (longPressRef.current?.fired) { longPressRef.current = null; return; }
          onOpenAppointment(app, event);
        }}
        onContextMenu={event => { event.preventDefault(); cancelLongPress(); onAppointmentContextMenu(app, event); }}
        onPointerDown={event => startLongPress(app, event)}
        onPointerUp={cancelLongPress}
        onPointerLeave={cancelLongPress}
        onPointerCancel={cancelLongPress}
        style={{
          background,
          color,
          border,
          boxShadow: roomColor ? `inset 4px 0 0 ${roomColor}` : undefined,
          opacity: isCanceled || isNoShow ? 0.75 : isPast ? 0.7 : 1
        }}
      >
        <span className="phone-cal-card-title" style={{ textDecoration: isCanceled ? 'line-through' : undefined }}>
          {getPatientName(app)}
          {showReservationDue && <span className="phone-cal-badge" style={{ background: '#dc2626', color: '#fff' }}>R!</span>}
        </span>
        <span className="phone-cal-card-meta">
          {timeLabel}{app.room ? ` · ${t('responsive.room')} ${app.room}` : ''}
        </span>
        {reason && <span className="phone-cal-card-meta">{reason}</span>}
      </button>
    );
  };

  const renderEvent = (ev: CalendarEvent) => {
    const start = new Date(ev.startTime!);
    const end = ev.endTime ? new Date(ev.endTime) : null;
    return (
      <button
        key={`e-${ev.id}-${ev.startTime}`}
        type="button"
        className="phone-cal-card"
        onClick={event => onEventClick(ev, event)}
        style={{ background: chrome.allDayBg, color: chrome.text, border: `1px dashed ${chrome.muted}` }}
      >
        <span className="phone-cal-card-title">{ev.title || t('responsive.calendar.newBlock')}</span>
        <span className="phone-cal-card-meta">{formatTime(start)}{end ? ` – ${formatTime(end)}` : ''}</span>
      </button>
    );
  };

  let lastMonthKey = '';

  return (
    <div className="phone-cal-schedule" data-testid="phone-schedule-view" style={{ background: chrome.panel, color: chrome.text }}>
      {sections.length === 0 && (
        <div className="phone-cal-empty" style={{ color: chrome.muted }}>{t('responsive.calendar.noAppointments')}</div>
      )}
      {sections.map(({ day, items, isToday }) => {
        const monthKey = `${day.getFullYear()}-${day.getMonth()}`;
        const showMonth = monthKey !== lastMonthKey;
        lastMonthKey = monthKey;
        const monthLabel = day.toLocaleDateString(i18n.language, {
          month: 'long',
          year: day.getFullYear() === today.getFullYear() ? undefined : 'numeric'
        });
        return (
          <React.Fragment key={day.toDateString()}>
            {showMonth && <div className="phone-cal-month-label" style={{ color: chrome.text }}>{monthLabel}</div>}
            <section className="phone-cal-schedule-day">
              <button type="button" className="phone-cal-schedule-date" onClick={() => onDayClick(day)} aria-label={day.toLocaleDateString(i18n.language, { weekday: 'long', day: 'numeric', month: 'long' })}>
                <span className="phone-cal-weekday" style={{ color: isToday ? tokens.app.primary : chrome.muted }}>
                  {day.toLocaleDateString(i18n.language, { weekday: 'short' }).replace('.', '')}
                </span>
                <span className="phone-cal-daynum" style={isToday ? { background: tokens.app.primary, color: tokens.app.primaryText } : { color: chrome.text }}>
                  {day.getDate()}
                </span>
              </button>
              <div className="phone-cal-schedule-items">
                {items.length === 0 && (
                  <div className="phone-cal-card-meta" style={{ color: chrome.muted, padding: '10px 0' }}>{t('responsive.calendar.noAppointments')}</div>
                )}
                {items.map(item => item.kind === 'appointment' ? renderAppointment(item.app, item.allDay) : renderEvent(item.ev))}
              </div>
            </section>
          </React.Fragment>
        );
      })}
      {onLoadMore && (
        <div ref={sentinelRef} className="phone-cal-load-more">
          <button type="button" onClick={onLoadMore} style={{ color: tokens.app.primary }}>{t('responsive.calendar.loadMore')}</button>
        </div>
      )}
    </div>
  );
};
